import React, { useState, useEffect } from 'react';
import { InfoModal, InfoButton, infoContent } from './InfoModal';

const handleNonNegativeInput = (e) => {
  const { value, min, max } = e.target;
  const numValue = parseFloat(value);
  
  if (value === '') {
    return '';
  }
  
  if (numValue < 0) {
    return 0;
  }
  
  if (max && numValue > parseFloat(max)) {
    return parseFloat(max);
  }
  
  if (min && numValue < parseFloat(min)) {
    return parseFloat(min);
  }
  
  return numValue;
};

const OutdoorUsage = ({ formData, updateFormData }) => {
  const [openSection, setOpenSection] = useState(null);
  
  // Add state for the info modal
  const [modalInfo, setModalInfo] = useState({
    isOpen: false,
    title: '',
    content: null,
    image: null
  });

  // Function to open the info modal
  const openInfoModal = (infoType) => {
    const info = infoContent[infoType];
    setModalInfo({
      isOpen: true,
      title: info.title,
      content: info.content,
      image: info.image
    });
  };

  // Function to close the info modal
  const closeInfoModal = () => {
    setModalInfo({
      ...modalInfo,
      isOpen: false
    });
  };
  
  // Show garden section open by default if user has garden
  useEffect(() => {
    if (formData?.hasGarden && !openSection) {
      setOpenSection('garden');
    }
  }, [formData?.hasGarden, openSection]);

  const handleInputChange = (e) => {
    const { name, type } = e.target;
    let value;

    if (type === 'number') {
      value = handleNonNegativeInput(e);
    } else {
      value = e.target.value;
    }

    updateFormData('outdoor', { [name]: value });
  };

  const toggleSection = (section) => {
    if (openSection === section) {
      setOpenSection(null);
    } else {
      setOpenSection(section);
    }
  };

  const isSectionFilled = (sectionId) => {
    switch(sectionId) {
      case 'garden':
        return formData?.gardenWateringMethod !== undefined;
      case 'lawn':
        return formData?.lawnWateringFrequency > 0;
      case 'pool':
        return formData?.hasPool !== undefined;
      case 'car':
        return formData?.carWashMethod !== undefined;
      default:
        return false;
    }
  };

  const CheckIcon = () => (
    <svg 
      className="h-5 w-5 text-green-500" 
      viewBox="0 0 20 20" 
      fill="currentColor"
    >
      <path 
        fillRule="evenodd" 
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
        clipRule="evenodd" 
      />
    </svg>
  );

  const renderSection = (section, content) => (
    <div className="border rounded-lg overflow-hidden mb-4">
      <button
        onClick={() => toggleSection(section.id)}
        className="w-full px-4 py-3 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-left flex items-center gap-2">
            {section.label}
          </h3>
          {isSectionFilled(section.id) && (
            <CheckIcon />
          )}
        </div>
        <svg
          className={`w-6 h-6 transform transition-transform ${
            openSection === section.id ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {openSection === section.id && (
        <div className="px-4 py-3 bg-gray-50 border-t">
          {content}
        </div>
      )}
    </div>
  );

  return (
    <div className="section-container max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-blue-700">Outdoor Water Usage</h2>
      <p className="text-gray-600 mb-6">
        Please provide information about your outdoor water usage.
      </p>

      {/* Add Info Modal Component */}
      <InfoModal
        isOpen={modalInfo.isOpen}
        onClose={closeInfoModal}
        title={modalInfo.title}
        content={modalInfo.content}
        image={modalInfo.image}
      />

      <div className="space-y-4">
        {/* Potted Plants Section */}
        {renderSection(
          { id: 'garden', label: '🌱 Potted Plants' },
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Do you have potted plants?
              </label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div
                  onClick={() => updateFormData('outdoor', { hasGarden: true })}
                  className={`p-2 border-2 rounded-lg cursor-pointer text-center
                    ${formData?.hasGarden === true
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'}`}
                >
                  Yes
                </div>
                <div
                  onClick={() => updateFormData('outdoor', { hasGarden: false })}
                  className={`p-2 border-2 rounded-lg cursor-pointer text-center
                    ${formData?.hasGarden === false
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'}`}
                >
                  No
                </div>
              </div>
            </div>
            
            {formData?.hasGarden && (
              <>
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    How do you water your plants?
                    <InfoButton onClick={() => openInfoModal('gardenWatering')} />
                  </label>
                  <div className="grid grid-cols-1 gap-2 mt-2">
                    {[
                      { id: 'wateringCan', label: 'Watering can/bucket' },
                      { id: 'hoseBasic', label: 'Garden hose' },
                      { id: 'hoseNozzle', label: 'Hose with nozzle/sprinkler' }
                    ].map(method => (
                      <div key={method.id}>
                        <div
                          onClick={() => updateFormData('outdoor', { gardenWateringMethod: method.id })}
                          className={`p-2 border-2 rounded-lg cursor-pointer
                            ${formData?.gardenWateringMethod === method.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300'}`}
                        >
                          {method.label}
                        </div>
                        
                        {/* Show follow-up question directly under the selected option */}
                        {formData?.gardenWateringMethod === method.id && (
                          <div className="mt-2 pl-2 border-l-2 pl-4 border-blue-200">
                            {method.id === 'wateringCan' && (
                              <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  How many buckets/cans do you typically use each time?
                                </label>
                                <div className="flex items-center gap-4">
                                  <input
                                    type="range"
                                    name="gardenWateringCans"
                                    min="1"
                                    max="20"
                                    step="1"
                                    value={formData?.gardenWateringCans || 0}
                                    onChange={handleInputChange}
                                    className="w-full"
              
                                  />
                                  
                                  <input
                                    type="number"
                                    name="gardenWateringCans"
                                    min="1"
                                    max="20"
                                    value={formData?.gardenWateringCans || 0}
                                    onChange={handleInputChange}
                                    className="w-20 text-center border rounded p-1"
                                  />
                            
                                </div>
                              </div>
                            )}
                            {(method.id === 'hoseBasic' || method.id === 'hoseNozzle') && (
                              <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  How many minutes do you typically water each time?
                                </label>
                                <div className="flex items-center gap-4">
                                  <input
                                    type="range"
                                    name="gardenWateringMinutes"
                                    min="1"
                                    max="60"
                                    step="1"
                                    value={formData?.gardenWateringMinutes || 0}
                                    onChange={handleInputChange}
                                    className="w-full"
                                  />
                                  <input
                                    type="number"
                                    name="gardenWateringMinutes"
                                    min="1"
                                    max="60"
                                    value={formData?.gardenWateringMinutes || 0}
                                    onChange={handleInputChange}
                                    className="w-20 text-center border rounded p-1"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    <span 
                      className="text-blue-600 hover:underline cursor-pointer"
                      onClick={() => openInfoModal('gardenWatering')}
                    >
                      Learn more about efficient plant watering
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Lawn/Garden Watering Section */}
        {renderSection(
          { id: 'lawn', label: '🌿 Lawn/Garden' },
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Do you have a lawn or garden?
              </label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div
                  onClick={() => updateFormData('outdoor', { hasLawn: true })}
                  className={`p-2 border-2 rounded-lg cursor-pointer text-center
                    ${formData?.hasLawn === true
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'}`}
                >
                  Yes
                </div>
                <div
                  onClick={() => updateFormData('outdoor', { hasLawn: false })}
                  className={`p-2 border-2 rounded-lg cursor-pointer text-center
                    ${formData?.hasLawn === false
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'}`}
                >
                  No
                </div>
              </div>
            </div>
            
            {formData?.hasLawn && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Approximate lawn/garden area (square feet):
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      name="lawnSqFt"
                      min="100"
                      max="10000"
                      step="100"
                      value={formData?.lawnSqFt || 0}
                      onChange={handleInputChange}
                      className="w-full"
                    />
                    <input
                      type="number"
                      name="lawnSqFt"
                      min="100"
                      max="10000"
                      value={formData?.lawnSqFt || 0}
                      onChange={handleInputChange}
                      className="w-24 text-center border rounded p-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Watering method:
                    <InfoButton onClick={() => openInfoModal('lawnWatering')} />
                  </label>
                  <div className="grid grid-cols-1 gap-2 mt-2">
                    {[
                      { id: 'hoseBasic', label: 'Standard hose' },
                      { id: 'hoseNozzle', label: 'Hose with nozzle/sprinkler' },
                      { id: 'automatic', label: 'Automatic sprinkler system' },
                      { id: 'drip', label: 'Drip irrigation system' }
                    ].map(method => (
                      <div key={method.id}>
                        <div
                          onClick={() => updateFormData('outdoor', { lawnWateringMethod: method.id })}
                          className={`p-2 border-2 rounded-lg cursor-pointer
                            ${formData?.lawnWateringMethod === method.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300'}`}
                        >
                          {method.label}
                        </div>
                        
                        {/* Show minutes input directly below the selected method */}
                        {formData?.lawnWateringMethod === method.id && (
                          <div className="mt-2 pl-2 border-l-2 border-blue-200">
                            <div className="mt-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                How many minutes do you typically water each time?
                              </label>
                              <div className="flex items-center gap-4">
                                <input
                                  type="range"
                                  name="lawnWateringMinutes"
                                  min="5"
                                  max="120"
                                  step="5"
                                  value={formData?.lawnWateringMinutes || 0}
                                  onChange={handleInputChange}
                                  className="w-full"
                                />
                                <input
                                  type="number"
                                  name="lawnWateringMinutes"
                                  min="5"
                                  max="120"
                                  value={formData?.lawnWateringMinutes || 0}
                                  onChange={handleInputChange}
                                  className="w-20 text-center border rounded p-1"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    <span 
                      className="text-blue-600 hover:underline cursor-pointer"
                      onClick={() => openInfoModal('lawnWatering')}
                    >
                      Compare watering system efficiency
                    </span>
                  </div>
                </div>

                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    How often do you water your lawn?
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      name="lawnWateringFrequency"
                      min="0"
                      max="7"
                      step="1"
                      value={formData?.lawnWateringFrequency || 0}
                      onChange={handleInputChange}
                      className="w-full"
                    />
                    <input
                      type="number"
                      name="lawnWateringFrequency"
                      min="0"
                      max="7"
                      value={formData?.lawnWateringFrequency || 0}
                      onChange={handleInputChange}
                      className="w-20 text-center border rounded p-1"
                    />
                    <span className="text-sm text-gray-600">times per week</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    When do you typically water your lawn/garden?
                  </label>
                  <div className="grid grid-cols-1 gap-2 mt-2">
                    {[
                      { id: 'morning', label: 'Early morning (most efficient)' },
                      { id: 'evening', label: 'Evening' },
                      { id: 'midday', label: 'Midday (least efficient)' },
                      { id: 'anytime', label: 'Anytime/varies' }
                    ].map(time => (
                      <div
                        key={time.id}
                        onClick={() => updateFormData('outdoor', { lawnWateringTime: time.id })}
                        className={`p-2 border-2 rounded-lg cursor-pointer
                          ${formData?.lawnWateringTime === time.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300'}`}
                      >
                        {time.label}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Pool Section */}
        {renderSection(
          { id: 'pool', label: '🏊 Swimming Pool' },
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Do you have a swimming pool?
              </label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div
                  onClick={() => updateFormData('outdoor', { hasPool: true })}
                  className={`p-2 border-2 rounded-lg cursor-pointer text-center
                    ${formData?.hasPool === true
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'}`}
                >
                  Yes
                </div>
                <div
                  onClick={() => updateFormData('outdoor', { hasPool: false })}
                  className={`p-2 border-2 rounded-lg cursor-pointer text-center
                    ${formData?.hasPool === false
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'}`}
                >
                  No
                </div>
              </div>
            </div>
            
            {formData?.hasPool && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pool size:
                  </label>
                  <div className="grid grid-cols-1 gap-2 mt-2">
                    {[
                      { id: 'small', label: 'Small (up to 37,850 liters)' },
                      { id: 'medium', label: 'Medium (37,850-75,700 liters)' },
                      { id: 'large', label: 'Large (75,700+ liters)' }
                    ].map(size => (
                      <div
                        key={size.id}
                        onClick={() => updateFormData('outdoor', { poolSize: size.id })}
                        className={`p-2 border-2 rounded-lg cursor-pointer
                          ${formData?.poolSize === size.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300'}`}
                      >
                        {size.label}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    Do you use a pool cover?
                    <InfoButton onClick={() => openInfoModal('poolMaintenance')} />
                  </label>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div
                      onClick={() => updateFormData('outdoor', { poolCover: true })}
                      className={`p-2 border-2 rounded-lg cursor-pointer text-center
                        ${formData?.poolCover === true
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'}`}
                    >
                      Yes
                    </div>
                    <div
                      onClick={() => updateFormData('outdoor', { poolCover: false })}
                      className={`p-2 border-2 rounded-lg cursor-pointer text-center
                        ${formData?.poolCover === false
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'}`}
                    >
                      No
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    <span 
                      className="text-blue-600 hover:underline cursor-pointer"
                      onClick={() => openInfoModal('poolMaintenance')}
                    >
                      Learn about pool water conservation
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    How often do you need to top off your pool? (times per month in summer)
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      name="poolRefillFrequency"
                      min="0"
                      max="10"
                      step="1"
                      value={formData?.poolRefillFrequency || 0}
                      onChange={handleInputChange}
                      className="w-full"
                    />
                    <input
                      type="number"
                      name="poolRefillFrequency"
                      min="0"
                      max="10"
                      value={formData?.poolRefillFrequency || 0}
                      onChange={handleInputChange}
                      className="w-20 text-center border rounded p-1"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Car Washing Section */}
        {renderSection(
          { id: 'car', label: '🚗 Car Washing' },
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                How do you typically wash your car?
                <InfoButton onClick={() => openInfoModal('carWashing')} />
              </label>
              <div className="grid grid-cols-1 gap-2 mt-2">
                {[
                  { id: 'hoseBasic', label: 'Standard hose' },
                  { id: 'hoseNozzle', label: 'Hose with nozzle' },
                  { id: 'bucket', label: 'Bucket and cloth' },
                  { id: 'never', label: "I don't have a car" }
                ].map(method => (
                  <div key={method.id}>
                    <div
                      onClick={() => updateFormData('outdoor', { carWashMethod: method.id })}
                      className={`p-2 border-2 rounded-lg cursor-pointer
                        ${formData?.carWashMethod === method.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'}`}
                    >
                      {method.label}
                    </div>
                    
                    {/* Show follow-up questions directly under the selected method */}
                    {formData?.carWashMethod === method.id && method.id !== 'never' && (
                      <div className="mt-2 pl-2 border-l-2 border-blue-200">
                        <div className="mt-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            How many times per month do you wash your car?
                          </label>
                          <div className="flex items-center gap-4">
                            <input
                              type="range"
                              name="carWashFrequency"
                              min="0"
                              max="30"
                              step="1"
                              value={formData?.carWashFrequency || 0}
                              onChange={handleInputChange}
                              className="w-full"
                            />
                            <input
                              type="number"
                              name="carWashFrequency"
                              min="0"
                              max="30"
                              value={formData?.carWashFrequency || 0}
                              onChange={handleInputChange}
                              className="w-20 text-center border rounded p-1"
                            />
                            <span className="text-sm text-gray-600">times per month</span>
                          </div>
                        </div>
                        
                        {(method.id === 'hoseBasic' || method.id === 'hoseNozzle') && (
                          <div className="mt-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              How many minutes do you typically spend washing your car?
                            </label>
                            <div className="flex items-center gap-4">
                              <input
                                type="range"
                                name="carWashMinutes"
                                min="5"
                                max="60"
                                step="5"
                                value={formData?.carWashMinutes || 0}
                                onChange={handleInputChange}
                                className="w-full"
                              />
                              <input
                                type="number"
                                name="carWashMinutes"
                                min="5"
                                max="60"
                                value={formData?.carWashMinutes || 0}
                                onChange={handleInputChange}
                                className="w-20 text-center border rounded p-1"
                              />
                            </div>
                          </div>
                        )}
                        
                        {method.id === 'bucket' && (
                          <div className="mt-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              How many buckets do you typically use per wash?
                            </label>
                            <div className="flex items-center gap-4">
                              <input
                                type="range"
                                name="carWashBuckets"
                                min="0"
                                max="5"
                                step="1"
                                value={formData?.carWashBuckets || 0}
                                onChange={handleInputChange}
                                className="w-full"
                              />
                              <input
                                type="number"
                                name="carWashBuckets"
                                min="0"
                                max="5"
                                value={formData?.carWashBuckets || 0}
                                onChange={handleInputChange}
                                className="w-20 text-center border rounded p-1"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="text-xs text-gray-500 mt-1 text-right">
                <span 
                  className="text-blue-600 hover:underline cursor-pointer"
                  onClick={() => openInfoModal('carWashing')}
                >
                  Compare car washing methods
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Water Conservation Practices */}
        {renderSection(
          { id: 'conservation', label: '💧 Water Conservation' },
          <div className="space-y-4">
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                Do you collect rainwater for outdoor use?
                <InfoButton onClick={() => openInfoModal('waterConservation')} />
              </label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div
                  onClick={() => updateFormData('outdoor', { collectsRainwater: true })}
                  className={`p-2 border-2 rounded-lg cursor-pointer text-center
                    ${formData?.collectsRainwater === true
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'}`}
                >
                  Yes
                </div>
                <div
                  onClick={() => updateFormData('outdoor', { collectsRainwater: false })}
                  className={`p-2 border-2 rounded-lg cursor-pointer text-center
                    ${formData?.collectsRainwater === false
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'}`}
                >
                  No
                </div>
              </div>
            </div>

            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                Do you use mulch in your garden beds?
                <InfoButton onClick={() => openInfoModal('waterConservation')} />
              </label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div
                  onClick={() => updateFormData('outdoor', { usesMulch: true })}
                  className={`p-2 border-2 rounded-lg cursor-pointer text-center
                    ${formData?.usesMulch === true
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'}`}
                >
                  Yes
                </div>
                <div
                  onClick={() => updateFormData('outdoor', { usesMulch: false })}
                  className={`p-2 border-2 rounded-lg cursor-pointer text-center
                    ${formData?.usesMulch === false
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'}`}
                >
                  No
                </div>
              </div>
            </div>

            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                Do you check your outdoor water system for leaks regularly?
                <InfoButton onClick={() => openInfoModal('waterConservation')} />
              </label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div
                  onClick={() => updateFormData('outdoor', { checksLeaks: true })}
                  className={`p-2 border-2 rounded-lg cursor-pointer text-center
                    ${formData?.checksLeaks === true
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'}`}
                >
                  Yes
                </div>
                <div
                  onClick={() => updateFormData('outdoor', { checksLeaks: false })}
                  className={`p-2 border-2 rounded-lg cursor-pointer text-center
                    ${formData?.checksLeaks === false
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'}`}
                >
                  No
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-1 text-right">
                <span 
                  className="text-blue-600 hover:underline cursor-pointer"
                  onClick={() => openInfoModal('waterConservation')}
                >
                  Learn more about outdoor water conservation
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OutdoorUsage;